<script>
    import { onDestroy, onMount } from "svelte";

    import Select from "svelte-select/Select.svelte";

    import { isMobile, isUserStayOnTab } from "../../stores";

    import { fly } from "svelte/transition";

    import {
        SearchInput,
        SvelteTable,
        PageLoading,
        TableElementEditableStatus,
        MultiSearchSelect,
        ResetFilterLink,
        DefaultDoctorFilter,
        DoctorMultiSearchSelect,
        TableElementPatientName,
        SearchSelect,
    } from "../../Components/Common/index.svelte";

    import { apiMethods } from "../../Services/index.svelte";

    import {
        InsuranceType,
        encounterBillingType,
        DirectInsuranceType,
        encounterStatus,
        BillingStatus,
    } from "../../Utils/utilEnums.svelte";

    import {
        goToURL,
        replaceURL,
        getClaimStatus,
        getText,
        getTitle,
        getFormatDateAndTime,
        showError,
        isObjectEmpty,
        getInsuranceType,
        getUrlWithoutSearch,
        decodeSearchParams,
        encodeSearchParams,
        syncDefaultDoctorFilter,
        getEncounterStatus,
        getBillingStatus,
        useTableScrollMemory,
    } from "../../Utils/utilFunctions.svelte";

    import {
        user,
        insuranceFund,
        trafficAccidentAuthorities,
        workersCompensation,
    } from "../../stores";

    import { duplicateIcon, editIcon } from "../../Utils/utilConstants.svelte";

    import {
        cacheInsuranceFunds,
        cacheTrafficAccidentAuthorities,
        cacheWorkersCompensation,
        startRefreshTable,
        stopRefreshTable,
    } from "../../Utils/utilCache.svelte";

    import TableFooter from "../../Components/Layout/Table/TableFooter.svelte";

    import EncounterTableEncounterIdCell from "../PatientEncounter/components/EncounterTableEncounterIdCell.svelte";
    import SurgeonSelector from "../../Components/Common/SurgeonSelector.svelte";
    import SearchDateRangeInput from "../../Components/Common/SearchDateRangeInput.svelte";

    export let params = {};

    let isGettingPatientEncounters = false,
        isLoadingHealthFund = false,
        isLoadingAccountDoctors = false,
        returnPatientEncounterArr = [],
        accountDoctorsArr = [],
        filters = { status: 5, billingStatus: "0,1,2,3,4" },
        page = 0,
        pageSize = 25,
        count,
        filterSurgeonObj,
        showFilters = false;

    let searchInsuranceTypeArr = [],
        searchPrivateHealthFundArr = [],
        healthFundOptionsArr = [],
        searchTypeIfBillArr = [],
        isShowPrivateHealthFundSelect = false;

    let isSearchingSurgeon = false,
        searchAccountDoctorsArr = [],
        selectedAccountDoctors = [];

    let selectedDoctors = [];

    let searchWorkCoverAuthorityFundArr = [],
        workCoverAuthorityOptionsArr = [],
        isLoadingWorkCoverAuthorityFund = false,
        isShowWorkCoverAuthoritySelect = false;

    let searchTrafficAccidentFundArr = [],
        trafficAccidentOptionsArr = [],
        isLoadingTrafficAccidentAuthority = false,
        isShowTrafficAccidentSelect = false;

    let refreshTableInterval, scrollTableContainerRef;

    $: isUserDoctor = $user.role == 2;

    $: isUserDoctorOrPatient = $user.role == 1 || isUserDoctor;

    $: isUserEnableSeeAllFilters = $user.role >= 2;

    $: isUserEnableCreateAndEdit = $user.role > 1 && $user.role != 90;

    $: if (!$isUserStayOnTab) stopRefreshTable(refreshTableInterval);
    else
        refreshTableInterval = startRefreshTable(
            {
                tableIntervalRef: refreshTableInterval,
            },
            () => reloadPatientEncounter(),
        );

    const columns = [
        {
            key: "id",
            title: "Enc. ID",
            headerClass:
                "text-left pl-3 sortable-header-decoration rounded-tl-xl",
            class: (v) =>
                "px-3 text-left cursor-pointer" + _getRowBgColor(v.checked),
            renderComponent: {
                component: EncounterTableEncounterIdCell,
            },
            csvContent: (v) => getText(v.id),
        },
        {
            key: "patient",
            title: "Patient",
            headerClass:
                "text-left pl-3 cursor-pointer sortable-header-decoration",
            class: (v) =>
                "px-3 text-left cursor-pointer" + _getRowBgColor(v.checked),
            renderComponent: {
                component: TableElementPatientName,
                props: {
                    onFinish: () =>
                        loadPatientEncounters(
                            page * pageSize,
                            pageSize,
                            _getSearchParams(),
                        ),
                    clickTo: `/PatientEncounter/view`,
                    propertyName: "patient",
                    showUnderAndOverPaymentIcons: true,
                },
            },
            csvContent: (v) => _getName(v.patient),
        },
        {
            key: "doctor",
            title: "Doctor",
            headerClass:
                "text-left pl-3 cursor-pointer sortable-header-decoration",
            class: (v) =>
                "px-3 text-left cursor-pointer" + _getRowBgColor(v.checked),
            renderValue: (v) => _getDoctor(v.doctor),
        },
        {
            key: "surgeon",
            title: "Surgeon",
            headerClass: "text-left pl-3 cursor-default",
            class: (v) =>
                "px-3 text-left cursor-pointer" + _getRowBgColor(v.checked),
            renderValue: (v) => _getSurgeon(v.surgeon),
        },
        {
            key: "hospital",
            title: "Hospital",
            headerClass: "text-left pl-3 cursor-default",
            class: (v) =>
                "px-3 text-left cursor-pointer" + _getRowBgColor(v.checked),
            renderValue: (v) => _getHospital(v.hospital),
            csvContent: (v) => _getHospital(v.hospital, true),
        },
        {
            key: "scheduledDate",
            title: "Scheduled Date",
            headerClass:
                "text-left pl-3 cursor-pointer sortable-header-decoration",
            class: (v) =>
                "px-3 text-left cursor-pointer" + _getRowBgColor(v.checked),
            renderValue: (v) => _getScheduleDate(v.scheduledDate),
        },
        {
            key: "status",
            title: "Status",
            headerClass: "text-left pl-3 cursor-default",
            class: (v) => "px-3 text-left" + _getRowBgColor(v.checked),
            renderComponent: {
                component: TableElementEditableStatus,
                props: {
                    type: "patientEncounterStatus",
                    onFinish: () =>
                        loadPatientEncounters(
                            page * pageSize,
                            pageSize,
                            _getSearchParams(),
                        ),
                },
            },
            csvContent: (v) => getEncounterStatus(v.status),
        },
        {
            key: "billingStatus",
            title: "Billing Status",
            headerClass: "text-left pl-3 cursor-default",
            class: (v) => "px-3 text-left" + _getRowBgColor(v.checked),
            renderComponent: {
                component: TableElementEditableStatus,
                props: {
                    type: "patientEncounterBillingStatus",
                    onFinish: () =>
                        loadPatientEncounters(
                            page * pageSize,
                            pageSize,
                            _getSearchParams(),
                        ),
                },
            },
            csvContent: (v) => getBillingStatus(v.billingStatus),
        },
        {
            key: "claimStatus",
            title: "Claim Status",
            headerClass: "text-left pl-3 cursor-default",
            class: (v) =>
                "px-3 text-left cursor-pointer" + _getRowBgColor(v.checked),
            renderValue: (v) => v.invoices && v.invoices.length > 0 && v.invoices.some((i) => i.status == 2) ? getClaimStatus(v.claimStatus) : "-",
        },
        {
            key: "opvStatus",
            title: "OPV Status",
            headerClass: "text-left pl-3 cursor-default",
            class: (v) =>
                "text-left px-3 text-left cursor-pointer" +
                _getRowBgColor(v.checked),
            renderValue: (v) => _renderOPVStatus(v),
            csvContent: (v) => _renderOPVStatus(v, true),
        },
        {
            key: "buttons",
            title: "Actions",
            headerClass: "text-left pl-3 cursor-default rounded-tr-xl",
            class: (v) =>
                "px-3 text-left cursor-pointer" + _getRowBgColor(v.checked),
            renderValue: (v) => _renderActionIcons(v),
            csvExportHidden: true,
        },
    ];

    const sortableHeaderKeys = ["id", "scheduledDate", "patient", "doctor"];

    const { saveScroll, restoreScrollPosition } =
        useTableScrollMemory("DebtCollection");

    $: isLoading =
        isGettingPatientEncounters ||
        isLoadingHealthFund ||
        isLoadingAccountDoctors;

    onMount(async () => {
        getUrlWithoutSearch();

        if (params.query) await updateSearchParamsByQuery(params.query);

        filters = await syncDefaultDoctorFilter(filters, "doctorId");

        await loadPatientEncounters(
            page * pageSize,
            pageSize,
            _getSearchParams(),
        );

        // In order to get health func properly
        await loadInsuranceFund();
    });

    onDestroy(() => {
        stopRefreshTable(refreshTableInterval);
    });

    const updateSearchParamsByQuery = async (query) => {
        const arr = decodeSearchParams(query).split("&");

        if (arr.length) {
            if (isUserDoctorOrPatient) {
                if (isUserDoctor) await loadAccountDoctors();

                for (let i = 0; i < arr.length; i++) {
                    if (arr[i].includes("page="))
                        page = Number(arr[i].split("=")[1]);

                    if (arr[i].includes("size="))
                        pageSize = Number(arr[i].split("=")[1]);

                    if (arr[i].includes("surgeonId=") && isUserDoctor) {
                        filters.surgeonId = Number(arr[i].split("=")[1]);

                        filterSurgeonObj = accountDoctorsArr.filter(
                            (surgeon) => surgeon.id == filters.surgeonId,
                        )[0];
                    }

                    if (arr[i].includes("fromDate="))
                        filters.dateFrom = arr[i].split("=")[1];

                    if (arr[i].includes("toDate="))
                        filters.dateTo = arr[i].split("=")[1];

                    if (arr[i].includes("underpaid=1"))
                        filters.underpaid = true;

                    if (arr[i].includes("commsDisabled=1"))
                        filters.commsDisabled = true;

                }
            }

            if (isUserEnableSeeAllFilters) {
                for (let i = 0; i < arr.length; i++) {
                    if (arr[i].includes("page="))
                        page = Number(arr[i].split("=")[1]);

                    if (arr[i].includes("size="))
                        pageSize = Number(arr[i].split("=")[1]);

                    if (arr[i].includes("sort="))
                        filters.sort = arr[i].split("=")[1];

                    if (arr[i].includes("search="))
                        filters.search = arr[i].split("=")[1].toString();

                    // search encounter under patient - url from view patient details
                    if (arr[i].includes("patientId="))
                        filters.patientId = Number(arr[i].split("=")[1]);

                    if (arr[i].includes("encounterId="))
                        filters.encounterId = arr[i].split("=")[1].toString();

                    if (arr[i].includes("doctorId=")) {
                        if (isNaN(arr[i].split("=")[1])) {
                            filters.doctorId = arr[i].split("=")[1];
                            selectedDoctors = filters.doctorId
                                .split(",")
                                .map((id) => Number(id));
                        } else {
                            filters.doctorId = Number(arr[i].split("=")[1]);
                            selectedDoctors = [Number(filters.doctorId)];
                        }
                    }

                    if (arr[i].includes("fromDate="))
                        filters.dateFrom = arr[i].split("=")[1];

                    if (arr[i].includes("toDate="))
                        filters.dateTo = arr[i].split("=")[1];

                    if (arr[i].includes("patientInsuranceType=")) {
                        searchInsuranceTypeArr = arr[i]
                            .split("=")[1]
                            .split(",")
                            .map(Number);

                        handleInsuranceTypeChange(searchInsuranceTypeArr, {
                            requireReloadFunction: false,
                        });
                    }

                    if (arr[i].includes("patientInsuranceID="))
                        searchPrivateHealthFundArr = arr[i]
                            .split("=")[1]
                            .split(",");

                    if (arr[i].includes("workCoverAuthorityID="))
                        searchWorkCoverAuthorityFundArr = arr[i]
                            .split("=")[1]
                            .split(",");

                    if (arr[i].includes("trafficAccidentAuthorityID="))
                        searchTrafficAccidentFundArr = arr[i]
                            .split("=")[1]
                            .split(",");

                    if (arr[i].includes("surgeonIDs="))
                        selectedAccountDoctors = arr[i]
                            .split("=")[1]
                            .split(",");

                    if (arr[i].includes("typeOfBill=")) {
                        searchTypeIfBillArr = arr[i].split("=")[1].split(",");

                        handleTypeOfBillChange(searchTypeIfBillArr, {
                            requireReloadFunction: false,
                        });
                    }

                    if (arr[i].includes("status="))
                        filters.status = isNaN(arr[i].split("=")[1])
                            ? arr[i].split("=")[1]
                            : Number(arr[i].split("=")[1]);

                    if (arr[i].includes("billingStatus="))
                        filters.billingStatus = isNaN(arr[i].split("=")[1])
                            ? arr[i].split("=")[1]
                            : Number(arr[i].split("=")[1]);

                    if (arr[i].includes("underpaid=1"))
                        filters.underpaid = true;

                    if (arr[i].includes("commsDisabled=1"))
                        filters.commsDisabled = true;
                    
                }
            }

            updateURL();
        }
    };

    const loadInsuranceFund = async () => {
        isLoadingHealthFund = true;
        if (params.sort) params.sort = params.sort.toLowerCase();

        await cacheInsuranceFunds();

        _updateInsuranceFundsOptions();

        isLoadingHealthFund = false;
    };

    const loadWorkCoverAuthorityFund = async () => {
        isLoadingWorkCoverAuthorityFund = true;

        if (params.sort) params.sort = params.sort.toLowerCase();

        await cacheWorkersCompensation();

        _updateWorkCoverOptions();

        isLoadingWorkCoverAuthorityFund = false;
    };

    const loadTrafficAccidentFund = async () => {
        isLoadingTrafficAccidentAuthority = true;

        if (params.sort) params.sort = params.sort.toLowerCase();

        await cacheTrafficAccidentAuthorities();

        _updateTrafficAccidentOptions();

        isLoadingTrafficAccidentAuthority = false;
    };

    const loadAccountDoctors = async () => {
        isLoadingAccountDoctors = true;

        accountDoctorsArr = [];

        await apiMethods.getMethod
            .getAccountDoctors(0, 999999, {
                "filter[doctorId]": $user.id,
            })
            .then((res) => {
                if (res.results && res.results.length) {
                    res.results.map((accDoctor) => {
                        const { title, firstName, lastName, speciality } =
                            accDoctor;

                        accountDoctorsArr.push({
                            ...accDoctor,
                            displayName: `${getTitle(title)} ${firstName} ${lastName} (${speciality})`,
                        });
                    });
                }
            });

        isLoadingAccountDoctors = false;
    };

    const loadPatientEncounters = async (start, limit, params) => {
        isGettingPatientEncounters = true;
        if (params.sort) params.sort = params.sort.toLowerCase();

        await apiMethods.getMethod
            .getPatientEncounter(start, limit, params)
            .then((res) => _handlePatientEncounters(res))
            .then(() => _updateHeaderSortIcon());
    };

    const reloadPatientEncounter = async () => {
        saveTableScroll();
        await apiMethods.getMethod
            .getPatientEncounter(page * pageSize, pageSize, _getSearchParams())
            .then((res) => _handlePatientEncounters(res))
            .then(() => restoreTableScroll())
            .then(() => _updateHeaderSortIcon());
    };

    const saveTableScroll = () => {
        if (scrollTableContainerRef)
            saveScroll(
                scrollTableContainerRef.scrollTop,
                scrollTableContainerRef.scrollLeft,
            );
    };

    const restoreTableScroll = () => {
        if (!scrollTableContainerRef) return;

        scrollTableContainerRef.scrollTop = restoreScrollPosition().top;
        scrollTableContainerRef.scrollLeft = restoreScrollPosition().left;

        saveScroll();
    };

    const handleFilterSelect = () => {
        page = 0;

        updateURL();

        loadPatientEncounters(0, pageSize, _getSearchParams());
    };

    const handleInsuranceTypeChange = async (
        searchResult = [],
        { requireReloadFunction = true },
    ) => {
        searchInsuranceTypeArr = [...searchResult];

        if (!searchInsuranceTypeArr?.length) {
            isShowPrivateHealthFundSelect = false;
            searchPrivateHealthFundArr = [];

            isShowWorkCoverAuthoritySelect = false;
            searchWorkCoverAuthorityFundArr = [];

            isShowTrafficAccidentSelect = false;
            searchTrafficAccidentFundArr = [];
        } else {
            if (
                searchInsuranceTypeArr.length &&
                searchInsuranceTypeArr.includes(0)
            )
                isShowPrivateHealthFundSelect = true;
            else {
                isShowPrivateHealthFundSelect = false;
                searchPrivateHealthFundArr = [];
            }

            if (searchInsuranceTypeArr.includes(Number(16))) {
                isShowWorkCoverAuthoritySelect = true;

                await loadWorkCoverAuthorityFund();
            } else {
                isShowWorkCoverAuthoritySelect = false;
                searchWorkCoverAuthorityFundArr = [];
            }

            if (searchInsuranceTypeArr.includes(Number(17))) {
                isShowTrafficAccidentSelect = true;

                await loadTrafficAccidentFund();
            } else {
                isShowTrafficAccidentSelect = false;
                searchTrafficAccidentFundArr = [];
            }
        }

        requireReloadFunction && handleFilterSelect();
    };

    const handleTypeOfBillChange = (
        searchResult = [],
        { requireReloadFunction = true },
    ) => {
        searchTypeIfBillArr = [...searchResult];

        requireReloadFunction && handleFilterSelect();
    };

    const handleHealthFundTypeChange = (searchResult = []) => {
        searchPrivateHealthFundArr = [...searchResult];

        handleFilterSelect();
    };

    const handleWorkCoverAuthorityTypeChange = (searchResult = []) => {
        searchWorkCoverAuthorityFundArr = [...searchResult];

        handleFilterSelect();
    };

    const handleTrafficAccidentAuthorityTypeChange = (searchResult = []) => {
        searchTrafficAccidentFundArr = [...searchResult];

        handleFilterSelect();
    };

    const handleClickCell = (
        { target: { className, classList } },
        rowData,
        key,
    ) => {
        if (key == "buttons" && classList.contains("editIcon"))
            goToURL(`/PatientEncounter/edit/${rowData.id}`);
        else if (key == "buttons" && classList.contains("duplicateIcon"))
            goToURL(`/PatientEncounter/edit/${rowData.id}/isDuplicate=1`);
        else if (
            key !== "buttons" &&
            key !== "status" &&
            key != "billingStatus" &&
            rowData.id
        )
            goToURL(`/PatientEncounter/view/${rowData.id}`);
    };

    const handleClickCol = ({ target: { className } }, colData, key) => {
        if (sortableHeaderKeys.includes(key)) {
            if (filters.sort == key) filters.sort = `${key}Desc`;
            else filters.sort = key;
            updateURL();
            loadPatientEncounters(0, pageSize, _getSearchParams());
        }
    };

    const onPageChange = (e) => {
        page = Number(e.detail.page);

        updateURL();

        loadPatientEncounters(
            Number(e.detail.firstDataIndex),
            pageSize,
            _getSearchParams(),
        );
    };

    const onPageSizeChange = (newPageSize) => {
        pageSize = newPageSize;
        page = 0;

        updateURL();

        loadPatientEncounters(0, newPageSize, _getSearchParams());
    };

    const updateURL = () => {
        replaceURL(
            `/DebtCollection/page=${page}&size=${pageSize}${_getParamsString()}`,
        );
    };

    const handleSelectDoctor = () => {
        filters.doctorId = selectedDoctors.join(",");

        handleFilterSelect();
    };

    const clearFilterDoctor = () => {
        selectedDoctors = [];

        delete filters.doctorId;

        handleFilterSelect();
    };

    const handleSelectSurgeon = (e) => {
        filters.surgeonId = e.detail.id;

        handleFilterSelect();
    };

    const clearCurrentSurgeon = () => {
        filterSurgeonObj = null;

        if (filters.surgeonId) {
            delete filters.surgeonId;

            handleFilterSelect();
        }
    };

    const handleSelectSurgeonFromSearch = (searchResult = []) => {
        selectedAccountDoctors = [...searchResult];

        handleFilterSelect();
    };

    // HELPER FUNCTIONS
    const _handlePatientEncounters = (res) => {
        isGettingPatientEncounters = false;
        if (res.error) showError(res.error);
        else {
            returnPatientEncounterArr = [...res.results];
            count = res.total;
        }
    };

    const _getSearchParams = () => {
        let params = {
            "filter[simplified]": 1,
            "filter[billingStatus]": "0,1,2,3,4",
            "filter[status]": "5",
        };

        if (filters.sort) params.sort = filters.sort;

        if (isUserEnableSeeAllFilters) {
            if (filters.search) params.search = filters.search;

            if (filters.encounterId) params["filter[id]"] = filters.encounterId;

            if (filters.patientId)
                params["filter[patientId]"] = filters.patientId;

            if (filters.doctorId) params["filter[doctorId]"] = filters.doctorId;

            if(filters.status){
                params["filter[status]"] = filters.status;
            }

            if(filters.billingStatus){
                params["filter[billingStatus]"] = filters.billingStatus;
            }
        }

        if (isUserDoctor && filters.surgeonId)
            params["filter[surgeonId]"] = filters.surgeonId;

        if (filters.dateFrom)
            params["filter[scheduledDateFrom]"] = filters.dateFrom;

        if (filters.dateTo) params["filter[scheduledDateTo]"] = filters.dateTo;

        if (searchInsuranceTypeArr.length)
            params["filter[patientInsuranceType]"] =
                searchInsuranceTypeArr.join(",");

        if (searchPrivateHealthFundArr.length)
            params["filter[patientInsuranceID]"] =
                searchPrivateHealthFundArr.join(",");

        if (searchWorkCoverAuthorityFundArr.length)
            params["filter[patientWorkCoverAuthority]"] =
                searchWorkCoverAuthorityFundArr.join(",");

        if (searchTrafficAccidentFundArr.length)
            params["filter[patientTrafficAccidentAuthority]"] =
                searchTrafficAccidentFundArr.join(",");

        if (selectedAccountDoctors.length)
            params["filter[surgeonUniqueID]"] =
                selectedAccountDoctors.join(",");

        if (searchTypeIfBillArr.length)
            params["filter[typeOfBill]"] = searchTypeIfBillArr.join(",");

        if (filters.underpaid) params["filter[underpaid]"] = 1;

        if (filters.commsDisabled) params["filter[disableNotifications]"] = 1;

        return params;
    };

    const _getParamsString = () => {
        let str = "";
        const params = _getSearchParams();

        if (params) {
            if (isUserEnableSeeAllFilters) {
                if (params.sort) str += `&sort=${params.sort}`;

                if (params.search) str += `&search=${params.search}`;

                if (params["filter[id]"])
                    str += `&encounterId=${params["filter[id]"]}`;

                if (params["filter[patientId]"])
                    str += `&patientId=${params["filter[patientId]"]}`;

                if (params["filter[doctorId]"])
                    str += `&doctorId=${params["filter[doctorId]"]}`;
            }

            if (isUserDoctor && params["filter[surgeonId]"])
                str += `&surgeonId=${params["filter[surgeonId]"]}`;

            if (params["filter[scheduledDateFrom]"])
                str += `&fromDate=${params["filter[scheduledDateFrom]"]}`;

            if (params["filter[scheduledDateTo]"])
                str += `&toDate=${params["filter[scheduledDateTo]"]}`;

            if (params["filter[patientInsuranceType]"])
                str += `&patientInsuranceType=${params["filter[patientInsuranceType]"]}`;

            if (params["filter[patientInsuranceID]"])
                str += `&patientInsuranceID=${params["filter[patientInsuranceID]"]}`;

            if (params["filter[patientWorkCoverAuthority]"])
                str += `&workCoverAuthorityID=${params["filter[patientWorkCoverAuthority]"]}`;

            if (params["filter[patientTrafficAccidentAuthority]"])
                str += `&trafficAccidentAuthorityID=${params["filter[patientTrafficAccidentAuthority]"]}`;

            if (params["filter[surgeonUniqueID]"])
                str += `&surgeonIDs=${params["filter[surgeonUniqueID]"]}`;

            if (params["filter[typeOfBill]"])
                str += `&typeOfBill=${params["filter[typeOfBill]"]}`;

            if (params["filter[underpaid]"]) str += `&underpaid=1`;

            if (params["filter[status]"]) str += `&status=${params["filter[status]"]}`;

            if (params["filter[billingStatus]"]) str += `&billingStatus=${params["filter[billingStatus]"]}`;

            if (params["filter[disableNotifications]"])
                str += `&commsDisabled=1`;

            
        }

        return encodeSearchParams(str);
    };

    const _updateHeaderSortIcon = () => {
        let thRow = document.getElementsByTagName("th");
        const params = _getSearchParams();

        if (params) {
            setTimeout(() => {
                const upSpan = document.createElement("span");
                const downSpan = document.createElement("span");
                upSpan.innerHTML = `<i class="fas fa-sort-alpha-down" />`;
                upSpan.style = "font-size: 10px;";
                downSpan.innerHTML = `<i class="fas fa-sort-alpha-up-alt" />`;
                downSpan.style = "font-size: 10px;";

                ["id", "scheduledDate", "patient", "doctor"];

                Array.from(thRow).forEach((row) => {
                    if (
                        (params.sort == "id" &&
                            row.childNodes[0].data == "Encounter ID") ||
                        (params.sort == "scheduledDate" &&
                            row.childNodes[0].data == "Scheduled Date") ||
                        (params.sort == "patient" &&
                            row.childNodes[0].data == "Patient") ||
                        (params.sort == "doctor" &&
                            row.childNodes[0].data == "Doctor")
                    )
                        row.appendChild(upSpan);
                    else if (
                        (params.sort == "idDesc" &&
                            row.childNodes[0].data == "Encounter ID") ||
                        (params.sort == "scheduledDateDesc" &&
                            row.childNodes[0].data == "Scheduled Date") ||
                        (params.sort == "patientDesc" &&
                            row.childNodes[0].data == "Patient") ||
                        (params.sort == "doctorDesc" &&
                            row.childNodes[0].data == "Doctor")
                    )
                        row.appendChild(downSpan);
                });
            }, 0);
        }
    };

    const _getName = ({ firstName, lastName, title, middleName }) => {
        const name = `${firstName}${
            middleName ? " " + middleName : ""
        } ${lastName}`;

        return `${getTitle(title)} ${name}`;
    };

    const _getDoctor = ({ title, fullName, preferredName }) => {
        return `${getTitle(title)} ${fullName} ${
            preferredName ? "(" + preferredName + ")" : ""
        }`;
    };

    const _getSurgeon = (surgeon) => {
        if (!surgeon || isObjectEmpty(surgeon))
            return "Missing surgeon details";

        const { title, firstName, lastName } = surgeon;

        return `${title ? getTitle(title) : ""} ${firstName ?? ""} ${
            lastName ?? ""
        }`;
    };

    const _getHospital = (hospital, isCsvContent = false) => {
        if (hospital) {
            const { name, address, suburb, state, postcode, providerNumber } =
                hospital;

            return isCsvContent
                ? `${name} - ${address}, ${suburb}, ${state}, ${postcode} (${providerNumber})`
                : `<div class="dropdown dropdown-top dropdown-hover">
                    <p>${name}${state ? `, ${state}` : ""}</p>
                    <div class="dropdown-content text-sm menu p-2 shadow bg-base-100 rounded-box w-64">
                        ${address}, ${suburb}, ${state}, ${postcode} (${providerNumber})
                    </div>
                </div>`;
        } else return "No hospital";
    };

    const _getScheduleDate = (date) => {
        if (date) {
            const dateArr = date.split("T")[0].split("-");
            if (Number(dateArr[0]) < 1900) return "-";
            return getFormatDateAndTime(date);
        } else return "-";
    };

    const _renderOPVStatus = (encounter, isCsvContent = false) => {
        const { patient, opvStatusMedicare, opvStatusHealthFund } = encounter;

        const greenText = "text-greenDefault",
            redText = "text-red-400",
            grayText = "text-gray-400";

        // In this case no direct insurance types would be detected, use prev logic
        if (!patient || isObjectEmpty(patient)) {
            const validStatus = [1, 2, 3];

            if (
                !validStatus.includes(opvStatusMedicare) &&
                !validStatus.includes(opvStatusHealthFund)
            )
                return isCsvContent
                    ? "Not checked"
                    : `<span class="${grayText}">Not checked</span>`;

            const result = [];

            if (validStatus.includes(opvStatusMedicare))
                result.push(
                    isCsvContent
                        ? "Medicare"
                        : `<span class="${
                              opvStatusMedicare == 1 ? greenText : redText
                          }">Medicare</span>`,
                );

            if (validStatus.includes(opvStatusHealthFund))
                result.push(
                    isCsvContent
                        ? "Health Fund"
                        : `<span class="${
                              opvStatusHealthFund == 1 ? greenText : redText
                          }">Health Fund</span>`,
                );

            return result
                .toString()
                .replaceAll(",", isCsvContent ? "\r\n" : "<br>");
        }

        const { insuranceType } = patient;

        // OPV medicare NOT CHECKED
        if (!opvStatusMedicare || opvStatusMedicare == 0)
            return isCsvContent
                ? `${getInsuranceType(insuranceType)}`
                : `<span class="${grayText}">${getInsuranceType(insuranceType)}</span>`;

        // Private checks status of HealthFund
        if (insuranceType == DirectInsuranceType.Private)
            return isCsvContent
                ? "Private"
                : `<span class="${opvStatusHealthFund == 1 ? `${greenText}` : `${redText}`}">Private</span>`;

        // All other insurance types check status of Medicare
        return isCsvContent
            ? "Medicare"
            : `<span class="${opvStatusMedicare == 1 ? `${greenText}` : `${redText}`}">Medicare</span>`;
    };

    const _renderActionIcons = ({ billingStatus, isDeleted }) => {
        const showEditBtn = !isDeleted;
        const showDuplicateBtn =
            (billingStatus != 4 && billingStatus != 5) ||
            (billingStatus == 4 && isUserEnableCreateAndEdit);

        return !showEditBtn && !showDuplicateBtn
            ? ""
            : `
            <div class="flex flex-1 items-center justify-start gap-x-2">
                ${
                    showEditBtn
                        ? `<div class="editIcon tooltip tooltip-secondary tooltip-left flex items-center justify-center w-[30px] h-[30px] hover:rounded-full hover:bg-secondary hover:text-white" data-tip="Edit Patient Encounter">
                            <i class="editIcon ${editIcon}"></i>
                        </div>`
                        : ""
                }
                ${
                    showDuplicateBtn
                        ? `<div class="duplicateIcon tooltip tooltip-secondary tooltip-left flex items-center justify-center w-[30px] h-[30px] hover:rounded-full hover:bg-secondary hover:text-white" data-tip="Duplicate Patient Encounter">
                            <i class="duplicateIcon ${duplicateIcon}"></i>
                        </div>`
                        : ""
                }
            </div>
        `;
    };

    const toggleFilters = () => (showFilters = !showFilters);

    const filterTransition = (node, args) => fly(node, args);

    const _updateInsuranceFundsOptions = () => {
        healthFundOptionsArr = $insuranceFund.map((fund) => ({
            key: fund.id,
            value: fund.name,
        }));
    };

    const _updateWorkCoverOptions = () => {
        workCoverAuthorityOptionsArr = $workersCompensation.map((wc) => ({
            key: wc.id,
            value: wc.name,
        }));
    };

    const _updateTrafficAccidentOptions = () => {
        trafficAccidentOptionsArr = $trafficAccidentAuthorities.map((tac) => ({
            key: tac.id,
            value: tac.name,
        }));
    };

    const _getRowBgColor = (isChecked = false) =>
        isChecked ? " bg-slate-200" : "";
</script>

<div class="flex flex-col flex-1 gap-y-4">
    {#if $isMobile}
        <div class="flex flex-row justify-between">
            {#if isUserEnableSeeAllFilters}
                <button
                    class="btn btn-accent btn-sm w-32 flex flex-row gap-x-1"
                    on:click={toggleFilters}
                >
                    Filters
                    <i class="fa fa-plus mr-2 text-[14px]" aria-hidden="true" />
                    <i
                        style="font-size:14px"
                        class={showFilters ? "fa arrow-up" : "fa arrow-down"}
                        >&#xf104;
                    </i>
                </button>
            {/if}
        </div>

        {#if showFilters && isUserEnableSeeAllFilters}
            <div
                class="flex items-end w-auto items-start gap-4 flex-wrap"
                in:fly={{ duration: 200, y: -50 }}
            >
                <SearchInput
                    bind:value={filters.encounterId}
                    title="Encounter ID"
                    type="number"
                    inputFunc={() => {
                        if (!isNaN(filters.encounterId)) handleFilterSelect();
                    }}
                    id="encounterIdInput"
                />

                <SearchInput
                    bind:value={filters.search}
                    title="Search"
                    inputFunc={handleFilterSelect}
                />

                {#if isUserDoctor}
                    <div class="flex flex-col items-start borderB">
                        <p class="text-[11px] font-[500]">Surgeon/Referrer</p>

                        <div class="autoFillSelector w-60 mt-2">
                            <Select
                                bind:value={filterSurgeonObj}
                                items={accountDoctorsArr}
                                on:clear={clearCurrentSurgeon}
                                on:select={handleSelectSurgeon}
                                itemId="id"
                                label="displayName"
                                disabled={isLoadingAccountDoctors}
                                noOptionsMessage="No Surgeon/Referrer Found"
                                --background="#E5E7EB"
                                --border="none"
                                --border-focused="none"
                                --border-hover="none"
                                --list-z-index="3"
                            />
                        </div>
                    </div>
                {:else}
                    <DefaultDoctorFilter
                        bind:filter={filters}
                        filterPropertyName={"doctorId"}
                        onChangeFunc={() => {
                            selectedDoctors = filters["doctorId"].split(",");
                            handleFilterSelect();
                        }}
                    />

                    <DoctorMultiSearchSelect
                        bind:searchResult={selectedDoctors}
                        onChangeFunc={handleSelectDoctor}
                        inputClass="w-32"
                        zIndex="3"
                    />
                {/if}

                {#if !$user.settings?.disableDateRangeInput}
                    <SearchDateRangeInput
                        bind:startDate={filters.dateFrom}
                        bind:endDate={filters.dateTo}
                        title="Date Range"
                        inputClass="borderB w-[272px]"
                        on:change={handleFilterSelect}
                        showClearButton
                        disable={isGettingPatientEncounters}
                        showScrollYearOptions
                    />
                {:else}
                    <SearchInput
                        bind:value={filters.dateFrom}
                        title="Date from"
                        type="date"
                        inputClass="borderB"
                        inputFunc={handleFilterSelect}
                        isDisable={isGettingPatientEncounters}
                        maxDate={filters.dateTo ?? undefined}
                        showScrollYearOptions
                    />

                    <SearchInput
                        bind:value={filters.dateTo}
                        title="Date to"
                        type="date"
                        inputClass="borderB"
                        inputFunc={handleFilterSelect}
                        isDisable={isGettingPatientEncounters}
                        minDate={filters.dateFrom ?? undefined}
                        showScrollYearOptions
                    />
                {/if}

                <MultiSearchSelect
                    title="Insurance Type"
                    inputID="patientInsuranceType"
                    isDisabled={isGettingPatientEncounters}
                    optionsArr={InsuranceType}
                    bind:searchResult={searchInsuranceTypeArr}
                    onChangeFunc={handleInsuranceTypeChange}
                    inputClass="w-60"
                />

                {#if isShowPrivateHealthFundSelect}
                    <MultiSearchSelect
                        title="Private Health Fund"
                        inputID="patientHealthFundType"
                        isDisabled={isGettingPatientEncounters}
                        optionsArr={healthFundOptionsArr}
                        bind:searchResult={searchPrivateHealthFundArr}
                        onChangeFunc={handleHealthFundTypeChange}
                    />
                {/if}

                {#if isShowWorkCoverAuthoritySelect}
                    <MultiSearchSelect
                        title="Work Cover Authority"
                        inputID="consentDueWorkCoverAuthority"
                        isDisabled={isLoadingWorkCoverAuthorityFund}
                        optionsArr={workCoverAuthorityOptionsArr}
                        bind:searchResult={searchWorkCoverAuthorityFundArr}
                        onChangeFunc={handleWorkCoverAuthorityTypeChange}
                    />
                {/if}

                {#if isShowTrafficAccidentSelect}
                    <MultiSearchSelect
                        title="Traffic Accident Authority"
                        inputID="consentDueTrafficAccidentAuthority"
                        isDisabled={isLoadingTrafficAccidentAuthority}
                        optionsArr={trafficAccidentOptionsArr}
                        bind:searchResult={searchTrafficAccidentFundArr}
                        onChangeFunc={handleTrafficAccidentAuthorityTypeChange}
                    />
                {/if}

                <SurgeonSelector
                    isDisabled={isGettingPatientEncounters}
                    {selectedDoctors}
                    bind:selectedSurgeonIds={selectedAccountDoctors}
                    onChangeFunc={handleSelectSurgeonFromSearch}
                    inputClass="w-60"
                />

                <MultiSearchSelect
                    title="TypeOfBill"
                    inputID="encounterTypeOfBill"
                    isDisabled={isGettingPatientEncounters}
                    optionsArr={encounterBillingType}
                    bind:searchResult={searchTypeIfBillArr}
                    onChangeFunc={handleTypeOfBillChange}
                    inputClass="w-60"
                />

               <!--  <SearchSelect
                    bind:value={filters.badDebtor}
                    title="Bad Debtor"
                    onChangeFunc={handleFilterSelect}
                    isDisabled={isGettingPatientEncounters}
                >
                    <option value={Number(-1)}>All</option>
                    <option value={Number(0)}>Non-Bad Debtors</option>
                    <option value={Number(1)} selected> Bad Debtors </option>
                </SearchSelect> -->

                <div class="flex flex-row gap-x-[6px]">
                    <input
                        type="checkbox"
                        class="toggle toggle-secondary"
                        bind:checked={filters.underpaid}
                        id="underpaid"
                        on:change={handleFilterSelect}
                        disabled={isGettingPatientEncounters}
                    />
                    <label for="underpaid">Underpaid</label>
                </div>

                <div class="flex flex-row gap-x-[6px]">
                    <input
                        type="checkbox"
                        class="toggle toggle-secondary"
                        bind:checked={filters.commsDisabled}
                        id="disabledComms"
                        on:change={handleFilterSelect}
                        disabled={isGettingPatientEncounters}
                    />
                    <label for="disabledComms">Comms Disabled</label>
                </div>
            </div>
        {/if}

        <ResetFilterLink
            bind:filter={filters}
            specialVariables={{
                searchInsuranceTypeArr,
                searchTypeIfBillArr,
                searchPrivateHealthFundArr,
                selectedAccountDoctors,
            }}
            onResetFilter={() => {
                selectedAccountDoctors = [];
                searchInsuranceTypeArr = [];
                searchTypeIfBillArr = [];
                handleInsuranceTypeChange(searchInsuranceTypeArr, {
                    requireReloadFunction: false,
                });
                handleTypeOfBillChange(searchTypeIfBillArr, {
                    requireReloadFunction: false,
                });
                searchPrivateHealthFundArr = [];

                clearFilterDoctor();
            }}
        />
    {:else}
        <div
            class="flex flex-0 items-start {$isMobile
                ? 'flex-col-reverse gap-y-4'
                : 'justify-between'}"
            in:filterTransition={{ duration: 200, y: -50 }}
        >
            <div class="flex flex-row items-end justify-start flex-wrap gap-4">
                {#if isUserEnableSeeAllFilters}
                    <SearchInput
                        bind:value={filters.encounterId}
                        title="Encounter ID"
                        type="number"
                        inputFunc={() => {
                            if (!isNaN(filters.encounterId))
                                handleFilterSelect();
                        }}
                        id="encounterIdInput"
                    />

                    <SearchInput
                        bind:value={filters.search}
                        title="Search"
                        inputFunc={handleFilterSelect}
                    />

                    {#if isUserDoctor}
                        <div class="flex flex-col items-start borderB">
                            <p class="text-[11px] font-[500]">
                                Surgeon/Referrer
                            </p>

                            <div class="autoFillSelector w-60 mt-2">
                                <Select
                                    bind:value={filterSurgeonObj}
                                    items={accountDoctorsArr}
                                    on:clear={clearCurrentSurgeon}
                                    on:select={handleSelectSurgeon}
                                    itemId="id"
                                    label="displayName"
                                    disabled={isLoadingAccountDoctors}
                                    noOptionsMessage="No Surgeon/Referrer Found"
                                    --background="#E5E7EB"
                                    --border="none"
                                    --border-focused="none"
                                    --border-hover="none"
                                    --list-z-index="3"
                                />
                            </div>
                        </div>
                    {:else}
                        <DefaultDoctorFilter
                            bind:filter={filters}
                            filterPropertyName={"doctorId"}
                            onChangeFunc={() => {
                                selectedDoctors =
                                    filters["doctorId"].split(",");
                                handleFilterSelect();
                            }}
                        />

                        <DoctorMultiSearchSelect
                            bind:searchResult={selectedDoctors}
                            onChangeFunc={handleSelectDoctor}
                            inputClass="w-32"
                            zIndex="3"
                        />
                    {/if}

                     <SearchSelect
                        bind:value={filters.status}
                        title="Status"
                        onChangeFunc={handleFilterSelect}
                        isDisabled={isGettingPatientEncounters}
                    >
                        <option value="all">All Statuses</option>
                        {#each encounterStatus as { id, type }}
                            <option value={id}>{type}</option>
                        {/each}
                    </SearchSelect>

                    <SearchSelect
                        bind:value={filters.billingStatus}
                        title="Billing Status"
                        onChangeFunc={handleFilterSelect}
                        isDisabled={isGettingPatientEncounters}
                    >
                        <option value="0,1,2,3,4" selected>
                            All Except Completed
                        </option>
                        {#each BillingStatus as billingStatusItem}
                            <option value={billingStatusItem.id}>
                                {billingStatusItem.type}
                            </option>
                        {/each}
                    </SearchSelect>

                    {#if !$user.settings?.disableDateRangeInput}
                        <SearchDateRangeInput
                            bind:startDate={filters.dateFrom}
                            bind:endDate={filters.dateTo}
                            title="Date Range"
                            inputClass="borderB w-[272px]"
                            on:change={handleFilterSelect}
                            showClearButton
                            disable={isGettingPatientEncounters}
                            showScrollYearOptions
                        />
                    {:else}
                        <SearchInput
                            bind:value={filters.dateFrom}
                            title="Date from"
                            type="date"
                            inputClass="borderB"
                            inputFunc={handleFilterSelect}
                            isDisable={isGettingPatientEncounters}
                            maxDate={filters.dateTo ?? undefined}
                            showScrollYearOptions
                        />

                        <SearchInput
                            bind:value={filters.dateTo}
                            title="Date to"
                            type="date"
                            inputClass="borderB"
                            inputFunc={handleFilterSelect}
                            isDisable={isGettingPatientEncounters}
                            minDate={filters.dateFrom ?? undefined}
                            showScrollYearOptions
                        />
                    {/if}

                    <MultiSearchSelect
                        title="Insurance Type"
                        inputID="patientInsuranceType"
                        isDisabled={isGettingPatientEncounters}
                        optionsArr={InsuranceType}
                        bind:searchResult={searchInsuranceTypeArr}
                        onChangeFunc={handleInsuranceTypeChange}
                        inputClass="w-32"
                    />

                    {#if isShowPrivateHealthFundSelect}
                        <MultiSearchSelect
                            title="Private Health Fund"
                            inputID="patientHealthFundType"
                            isDisabled={isGettingPatientEncounters}
                            optionsArr={healthFundOptionsArr}
                            bind:searchResult={searchPrivateHealthFundArr}
                            onChangeFunc={handleHealthFundTypeChange}
                            inputClass="w-32"
                        />
                    {/if}

                    {#if isShowWorkCoverAuthoritySelect}
                        <MultiSearchSelect
                            title="Work Cover Authority"
                            inputID="consentDueWorkCoverAuthority"
                            isDisabled={isLoadingWorkCoverAuthorityFund}
                            optionsArr={workCoverAuthorityOptionsArr}
                            bind:searchResult={searchWorkCoverAuthorityFundArr}
                            onChangeFunc={handleWorkCoverAuthorityTypeChange}
                            inputClass="w-32"
                        />
                    {/if}

                    {#if isShowTrafficAccidentSelect}
                        <MultiSearchSelect
                            title="Traffic Accident Authority"
                            inputID="consentDueTrafficAccidentAuthority"
                            isDisabled={isLoadingTrafficAccidentAuthority}
                            optionsArr={trafficAccidentOptionsArr}
                            bind:searchResult={searchTrafficAccidentFundArr}
                            onChangeFunc={handleTrafficAccidentAuthorityTypeChange}
                            inputClass="w-32"
                        />
                    {/if}

                    <SurgeonSelector
                        isDisabled={isGettingPatientEncounters}
                        {selectedDoctors}
                        bind:selectedSurgeonIds={selectedAccountDoctors}
                        onChangeFunc={handleSelectSurgeonFromSearch}
                        inputClass="w-32"
                    />

                    <MultiSearchSelect
                        title="TypeOfBill"
                        inputID="encounterTypeOfBill"
                        isDisabled={isGettingPatientEncounters}
                        optionsArr={encounterBillingType}
                        bind:searchResult={searchTypeIfBillArr}
                        onChangeFunc={handleTypeOfBillChange}
                        inputClass="w-32"
                    />

                     <div class="flex flex-row gap-x-[6px]">
                        <input
                            type="checkbox"
                            class="toggle toggle-secondary"
                            bind:checked={filters.underpaid}
                            id="underpaid"
                            on:change={handleFilterSelect}
                            disabled={isGettingPatientEncounters}
                        />
                        <label for="underpaid">Underpaid</label>
                    </div>

                    <div class="flex flex-row gap-x-[6px]">
                        <input
                            type="checkbox"
                            class="toggle toggle-secondary"
                            bind:checked={filters.commsDisabled}
                            id="disabledComms"
                            on:change={handleFilterSelect}
                            disabled={isGettingPatientEncounters}
                        />
                        <label for="disabledComms">Comms Disabled</label>
                    </div>

                    <ResetFilterLink
                        bind:filter={filters}
                        specialVariables={{
                            searchInsuranceTypeArr,
                            searchTypeIfBillArr,
                            searchPrivateHealthFundArr,
                            selectedAccountDoctors,
                        }}
                        onResetFilter={() => {
                            selectedAccountDoctors = [];
                            searchInsuranceTypeArr = [];
                            searchTypeIfBillArr = [];
                            handleInsuranceTypeChange(searchInsuranceTypeArr, {
                                requireReloadFunction: false,
                            });
                            handleTypeOfBillChange(searchTypeIfBillArr, {
                                requireReloadFunction: false,
                            });
                            searchPrivateHealthFundArr = [];

                            clearFilterDoctor();
                        }}
                    />
                {/if}
            </div>
        </div>
    {/if}

    <div class={$isMobile ? "h-auto w-minus48" : "relative flex-1"}>
        {#if isLoading}
            <PageLoading />
        {:else}
            <div
                class={$isMobile
                    ? "overflow-x-auto overflow-y-visible"
                    : "overflow-y-auto absolute inset-0"}
                bind:this={scrollTableContainerRef}
            >
                <SvelteTable
                    {columns}
                    rows={returnPatientEncounterArr}
                    clickCellFunc={({ detail: { event, row, key } }) => {
                        handleClickCell(event, row, key);
                    }}
                    clickColFunc={({ detail: { event, row, key } }) => {
                        handleClickCol(event, row, key);
                    }}
                    customRow="align-middle"
                />
            </div>
        {/if}
    </div>

    <TableFooter
        {page}
        {pageSize}
        {count}
        tableData={returnPatientEncounterArr}
        {onPageChange}
        {onPageSizeChange}
        csvConfig={{
            columns,
            data: returnPatientEncounterArr,
            fileName: "Debt Collection",
            allDataApi: async () =>
                await apiMethods.getMethod.getPatientEncounter(
                    0,
                    999999,
                    _getSearchParams(),
                ),
        }}
    />
</div>
